import { useState } from 'react';
import { ActionIcon, Group, Text, TextInput, Title } from '@mantine/core';
import { useTranslation } from 'react-i18next';

export interface InlineRenameProps {
  value: string;
  canEdit: boolean;
  onSave: (name: string) => Promise<unknown>;
  /** Heading level for the read-only display. */
  order?: 1 | 2 | 3;
  /** Plain text instead of a heading, for table cells. */
  compact?: boolean;
}

/** A heading with a pencil: click to edit in place, Enter saves, Escape cancels. */
export function InlineRename({ value, canEdit, onSave, order = 2, compact = false }: InlineRenameProps) {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);

  const start = () => {
    setDraft(value);
    setEditing(true);
  };
  const save = async () => {
    const name = draft.trim();
    if (!name || name === value) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(name);
      setEditing(false);
    } catch {
      // Stay in edit mode; the caller is responsible for surfacing the error.
    } finally {
      setSaving(false);
    }
  };

  if (editing) {
    return (
      <TextInput
        value={draft}
        autoFocus
        disabled={saving}
        maxLength={255}
        onChange={(e) => setDraft(e.currentTarget.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') void save();
          if (e.key === 'Escape') setEditing(false);
        }}
        onBlur={() => void save()}
        aria-label={t('common.name')}
        size={compact ? 'xs' : undefined}
      />
    );
  }
  return (
    <Group gap="xs" align="center">
      {compact ? <Text fz={14}>{value}</Text> : <Title order={order}>{value}</Title>}
      {canEdit && (
        <ActionIcon variant="subtle" color="forest" size={compact ? 'sm' : undefined} aria-label={t('common.rename')} onClick={start}>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M11.5 2.5l2 2L5 13H3v-2l8.5-8.5z" strokeLinejoin="round" />
          </svg>
        </ActionIcon>
      )}
    </Group>
  );
}
